import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Icon } from './Icon';
import { InputTextField as InputStandard } from './InputStandard';
import { SortableHeader } from './SortableHeader';
import { Pagination } from './Pagination';
import { osirisTokens } from '@/theme/tokens';

/** Reserved width for the hover-revealed row actions column. */
const ROW_ACTIONS_WIDTH = 88;

export interface DataTableColumn<T> {
  field: string;
  label: string | ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render?: (row: T, index: number) => ReactNode;
  headerSx?: object;
  cellSx?: object;
}

interface DataTableProps<T> {
  // Data
  data: T[];
  columns: DataTableColumn<T>[];

  // Loading & Error states
  isLoading?: boolean;
  error?: boolean;
  emptyMessage?: string;
  /** Rich empty-state content rendered in place of `emptyMessage` when there is no data. */
  emptyState?: ReactNode;

  // Search
  searchLabel?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;

  // Extra filter controls rendered next to the search input
  filterContent?: ReactNode;

  // Action button (e.g., "Create")
  actionButton?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };

  // Sorting
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;

  // Pagination
  page?: number;
  pageSize?: number;
  totalCount?: number;
  hasMore?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];

  /**
   * Renders an actions slot on the right side of each row that's only visible on hover.
   * Typically used for a kebab/MoreVert menu trigger.
   */
  rowActions?: (row: T) => ReactNode;
}

export function DataTable<T extends { id: string }>({
  data,
  columns,
  isLoading = false,
  error = false,
  emptyMessage = 'No items found',
  emptyState,
  searchLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  actionButton,
  filterContent,
  sortBy,
  sortOrder,
  onSort,
  page,
  pageSize,
  totalCount,
  hasMore,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  rowActions,
}: DataTableProps<T>) {
  const columnsCount = columns.length + (rowActions ? 1 : 0);
  const showToolbar = onSearchChange || actionButton || filterContent;

  return (
    <Box
      className="data-table"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        flex: 1,
        width: 'min(100%, max(1200px, 100% - 600px))',
        marginRight: 'auto',
      }}
    >
      {showToolbar && (
        <Box
          className="data-table-toolbar"
          sx={{
            px: 2,
            py: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, minWidth: 0 }}>
            {onSearchChange && (
              <InputStandard
                className="data-table-search"
                placeholder={searchPlaceholder || searchLabel || 'Search...'}
                value={searchValue || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onSearchChange(e.target.value)
                }
                isDebounce={true}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <Icon
                          name="search"
                          size={20}
                          sx={{ color: osirisTokens.icons.secondary }}
                        />
                      </InputAdornment>
                    ),
                    // clear (×) once there is a query (Figma Text field 29794:83389)
                    ...(searchValue && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => onSearchChange('')}
                            className="data-table-search-clear"
                            sx={{
                              p: 0,
                              color: osirisTokens.icons.secondary,
                              '&:hover': {
                                bgcolor: 'transparent',
                                color: osirisTokens.text.active,
                              },
                            }}
                          >
                            <Icon name="close-S" size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }),
                  },
                }}
                sx={{
                  // Figma search field S (28376:54239): 32px tall, 12px / 8px
                  // side insets, 8px between the icon and the text
                  minWidth: { xs: 200, md: 280 },
                  minHeight: 32,
                  '& .MuiFormControl-root, &.MuiFormControl-root': { minHeight: 32 },
                  '& .MuiInputBase-root': { height: 32, pl: '12px', pr: 1 },
                  '& .MuiInputBase-root .MuiInputBase-input': { pl: 0 },
                }}
              />
            )}
            {filterContent}
          </Box>
          {actionButton && (
            <Button
              variant="contained"
              size="small"
              startIcon={actionButton.icon ?? <Add sx={{ fontSize: 16 }} />}
              onClick={actionButton.onClick}
              className="data-table-action"
              sx={{
                fontFamily: osirisTokens.fonts.body,
                fontSize: 13,
                height: 32,
                px: 1.5,
                flexShrink: 0,
              }}
            >
              {actionButton.label}
            </Button>
          )}
        </Box>
      )}

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          px: 2,
          bgcolor: 'transparent',
          overflowX: 'auto',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                if (column.sortable && onSort) {
                  return (
                    <SortableHeader
                      key={column.field}
                      field={column.field}
                      sortBy={sortBy || ''}
                      sortOrder={sortOrder}
                      onSort={onSort}
                      align={column.align}
                      sx={column.headerSx}
                    >
                      {column.label}
                    </SortableHeader>
                  );
                }
                return (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{
                      height: 24,
                      py: 0,
                      px: '12px',
                      borderBottom: 'none',
                      bgcolor: 'transparent',
                      fontFamily: osirisTokens.fonts.body,
                      fontSize: 11,
                      fontWeight: 400,
                      letterSpacing: '0.22px',
                      textTransform: 'uppercase',
                      color: osirisTokens.text.tertiary,
                      ...(column.width && { width: column.width }),
                      ...column.headerSx,
                    }}
                  >
                    {column.label}
                  </TableCell>
                );
              })}
              {rowActions && (
                <TableCell
                  aria-label="Actions"
                  sx={{
                    width: ROW_ACTIONS_WIDTH,
                    height: 24,
                    py: 0,
                    px: '12px',
                    borderBottom: 'none',
                    bgcolor: 'transparent',
                  }}
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columnsCount}
                  sx={{ border: 'none', textAlign: 'center', py: 4 }}
                >
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                {/* Figma table "error / S" state (28235:60537) */}
                <TableCell
                  colSpan={columnsCount}
                  className="data-table-error"
                  sx={{
                    border: 'none',
                    height: 40,
                    px: 2,
                    py: 1.5,
                    textAlign: 'center',
                    bgcolor: 'rgba(197, 53, 56, 0.12)',
                    borderRadius: '2px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: osirisTokens.fonts.mono,
                      fontSize: 11,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      color: '#f83e4d', // Colors/Status/error/cold-500
                    }}
                  >
                    [ Error loading data ]
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnsCount}
                  sx={
                    emptyState
                      ? { border: 'none', p: 0, bgcolor: 'rgba(255, 255, 255, 0.08)' }
                      : {
                          border: 'none',
                          textAlign: 'center',
                          py: 4,
                          bgcolor: 'rgba(255, 255, 255, 0.08)',
                        }
                  }
                >
                  {emptyState ?? (
                    <Typography
                      sx={{
                        fontFamily: osirisTokens.fonts.mono,
                        fontSize: 12,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: osirisTokens.text.muted,
                      }}
                    >
                      {emptyMessage}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={row.id}
                  className="data-table-row"
                  sx={{
                    position: 'relative',
                    bgcolor: osirisTokens.bg.header,
                    transition: 'background-color 100ms ease',
                    // swap the row bg — painting the tds on top of the row's
                    // 0.08 white stacked to ~0.25 and read too light vs the
                    // flat Bg/bg-table-hover in the mock (Figma 28666:17194)
                    '&:hover': { bgcolor: osirisTokens.bg.tableHover },
                    '&:hover .data-table-row-actions': { opacity: 1, pointerEvents: 'auto' },
                  }}
                >
                  {columns.map((column) => {
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align}
                        sx={{
                          height: 40,
                          py: 0,
                          px: '12px',
                          borderBottom: `1px solid ${osirisTokens.bg.page}`,
                          fontFamily: osirisTokens.fonts.body,
                          fontSize: 13,
                          color: osirisTokens.text.primary,
                          ...(column.width && { width: column.width }),
                          ...column.cellSx,
                        }}
                      >
                        {column.render
                          ? column.render(row, index)
                          : ((row as Record<string, unknown>)[column.field] as string) || '—'}
                      </TableCell>
                    );
                  })}
                  {/* Actions live in their own cell — overlaying the last column
                      clipped its text on hover (Figma To Do 28458:20689). */}
                  {rowActions && (
                    <TableCell
                      align="right"
                      sx={{
                        width: ROW_ACTIONS_WIDTH,
                        height: 40,
                        py: 0,
                        // buttons sit at the row's right edge (4px inset, as top/bottom)
                        pl: '12px',
                        pr: '4px',
                        borderBottom: `1px solid ${osirisTokens.bg.page}`,
                      }}
                    >
                      <Box
                        className="data-table-row-actions"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 100ms ease',
                        }}
                      >
                        {rowActions(row)}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {page !== undefined && pageSize !== undefined && onPageChange && onPageSizeChange && (
        <Pagination
          page={page}
          pageSize={pageSize}
          visibleCount={data.length}
          totalCount={totalCount}
          hasMore={hasMore}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </Box>
  );
}
